import { logger } from '../logger';
import type { SupportedLanguages } from '../../i18n';

const STORAGE_KEY = 'app_stay_dates';
const CHECKOUT_ALERT_SHOWN_KEY = 'checkout_alert_shown_date';

export interface StayDates {
  startDate: string; // ISO string
  endDate: string; // ISO string
  lastUpdated: string; // ISO string
}

class StayService {
  private static instance: StayService;
  private stayDates: StayDates | null = null;

  private constructor() {
    logger.debug('[STAY_SERVICE_INIT] Initializing StayService', {
      category: 'Stay',
      data: { timestamp: new Date().toISOString() }
    });
    this.loadStayDates();
  }

  public static getInstance(): StayService {
    if (!StayService.instance) {
      StayService.instance = new StayService();
    }
    return StayService.instance;
  }

  private loadStayDates(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        this.stayDates = JSON.parse(stored);
        
        logger.debug('[STAY_DATES_LOADED] Loaded stay dates from storage', {
          category: 'Stay',
          data: { 
            startDate: this.stayDates?.startDate,
            endDate: this.stayDates?.endDate,
            lastUpdated: this.stayDates?.lastUpdated
          }
        });
      } else {
        logger.debug('[STAY_DATES_EMPTY] No stay dates found in storage', {
          category: 'Stay',
          data: { storageKey: STORAGE_KEY }
        });
      }
    } catch (error) {
      logger.error('[STAY_DATES_ERROR] Error loading stay dates', {
        category: 'Stay',
        data: { error, storageKey: STORAGE_KEY }
      });
      this.stayDates = null;
    }
  }

  private saveStayDates(): void {
    try {
      if (this.stayDates) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.stayDates));
        
        logger.debug('[STAY_DATES_SAVED] Saved stay dates to storage', {
          category: 'Stay',
          data: { 
            startDate: this.stayDates.startDate,
            endDate: this.stayDates.endDate,
            lastUpdated: this.stayDates.lastUpdated
          }
        });
      } else {
        localStorage.removeItem(STORAGE_KEY);
        logger.debug('[STAY_DATES_REMOVED] Removed stay dates from storage', {
          category: 'Stay',
          data: { storageKey: STORAGE_KEY }
        });
      }
    } catch (error) {
      logger.error('[STAY_DATES_SAVE_ERROR] Error saving stay dates', {
        category: 'Stay',
        data: { error, storageKey: STORAGE_KEY }
      });
    }
  }

  public getStayDates(): StayDates | null {
    logger.debug('[STAY_DATES_GET] Getting stay dates', {
      category: 'Stay',
      data: { 
        hasDates: !!this.stayDates,
        dates: this.stayDates
      }
    });
    return this.stayDates;
  }

  public setStayDates(startDate: Date, endDate: Date): void {
    logger.debug('[STAY_DATES_SET] Setting stay dates', {
      category: 'Stay',
      data: { 
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      }
    });
    
    this.stayDates = {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      lastUpdated: new Date().toISOString()
    };
    this.saveStayDates();
    
    // Clear the checkout alert shown flag when setting new dates
    localStorage.removeItem(CHECKOUT_ALERT_SHOWN_KEY);
  }

  public hasStayDates(): boolean {
    const hasDates = this.stayDates !== null;
    
    logger.debug('[STAY_DATES_CHECK] Checking if stay dates exist', {
      category: 'Stay',
      data: { hasDates }
    });
    
    return hasDates;
  }

  public clearStayDates(): void {
    logger.debug('[STAY_DATES_CLEAR] Clearing stay dates', {
      category: 'Stay',
      data: { 
        hadDates: !!this.stayDates,
        oldDates: this.stayDates
      }
    });
    
    this.stayDates = null;
    this.saveStayDates();
    
    // Clear the checkout alert shown flag when clearing dates
    localStorage.removeItem(CHECKOUT_ALERT_SHOWN_KEY);
  }

  public getDefaultDates(): { startDate: Date; endDate: Date } {
    const now = new Date();
    
    // Start date is today at current time
    const startDate = new Date(now);
    
    // End date is tomorrow at midnight
    const endDate = new Date(now);
    endDate.setDate(endDate.getDate() + 1);
    endDate.setHours(23, 59, 59, 999);
    
    logger.debug('[STAY_DATES_DEFAULT] Generated default stay dates', {
      category: 'Stay',
      data: { 
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      }
    });
    
    return { startDate, endDate };
  }

  public formatStayDates(language: SupportedLanguages): { formattedStart: string; formattedEnd: string } | null {
    if (!this.stayDates) return null;
    
    const startDate = new Date(this.stayDates.startDate);
    const endDate = new Date(this.stayDates.endDate);
    
    const formattedStart = new Intl.DateTimeFormat(language, {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }).format(startDate);
    
    const formattedEnd = new Intl.DateTimeFormat(language, {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }).format(endDate);
    
    return { formattedStart, formattedEnd };
  }

  public areDatesExpired(): boolean {
    if (!this.stayDates) return false;
    
    const now = new Date();
    const endDate = new Date(this.stayDates.endDate);
    
    const isExpired = endDate < now;
    
    logger.debug('[STAY_DATES_EXPIRED_CHECK] Checking if stay dates are expired', {
      category: 'Stay',
      data: { 
        isExpired,
        now: now.toISOString(),
        endDate: endDate.toISOString(),
        stayDates: this.stayDates
      }
    });
    
    return isExpired;
  }

  /**
   * Checks if today is the checkout day (last day of stay)
   * @returns boolean indicating if today is the checkout day
   */
  public isCheckoutDay(): boolean {
    if (!this.stayDates) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    
    const endDate = new Date(this.stayDates.endDate);
    endDate.setHours(0, 0, 0, 0); // Reset time to start of day
    
    // Check if today is the checkout day
    const isCheckout = today.getTime() === endDate.getTime();
    
    logger.debug('[STAY_DATES_CHECKOUT_CHECK] Checking if today is checkout day', {
      category: 'Stay',
      data: { 
        isCheckout,
        today: today.toISOString(),
        endDate: endDate.toISOString(),
        stayDates: this.stayDates
      }
    });
    
    return isCheckout;
  }

  /**
   * Checks if checkout alert has been shown today
   * @returns boolean indicating if checkout alert has been shown today
   */
  public hasCheckoutAlertBeenShownToday(): boolean {
    const lastShown = localStorage.getItem(CHECKOUT_ALERT_SHOWN_KEY);
    if (!lastShown) return false;
    
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    const hasShown = lastShown === today;
    
    logger.debug('[STAY_DATES_CHECKOUT_ALERT_CHECK] Checking if checkout alert has been shown today', {
      category: 'Stay',
      data: { 
        hasShown,
        lastShown,
        today
      }
    });
    
    return hasShown;
  }

  /**
   * Marks checkout alert as shown for today
   */
  public markCheckoutAlertAsShown(): void {
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    localStorage.setItem(CHECKOUT_ALERT_SHOWN_KEY, today);
    
    logger.debug('[STAY_DATES_CHECKOUT_ALERT_MARKED] Marked checkout alert as shown', {
      category: 'Stay',
      data: { 
        date: today
      }
    });
  }
}

export const stayService = StayService.getInstance();